import type { CSSProperties } from 'react';
import Chip from '@material-ui/core/Chip';

export type Tone =
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'purple'
  | 'neutral';

/**
 * Tone colors as CSS variables: the app theme defines `--bobbee-tone-*` per light/dark mode
 * (packages/app/src/theme/bui.css); the fallbacks apply where no theme CSS is loaded (tests).
 * Use `tint(color, percent)` for tinted backgrounds, never hex alpha suffixes.
 */
export const TONE_COLORS: Record<Tone, string> = {
  success: 'var(--bobbee-tone-success, #067647)',
  error: 'var(--bobbee-tone-error, #b42318)',
  warning: 'var(--bobbee-tone-warning, #b54708)',
  info: 'var(--bobbee-tone-info, #175cd3)',
  purple: 'var(--bobbee-tone-purple, #6941c6)',
  neutral: 'var(--bobbee-tone-neutral, #667085)',
};

/**
 * Secondary text (dates, branches, hints): the theme's secondary foreground, readable in light
 * and dark mode (AA contrast), never a fixed gray.
 */
export const TEXT_MUTED = 'var(--bui-fg-secondary, #4a5565)';

/** Text color on a solid tone background (white in light mode, near-black in dark mode). */
export const ON_TONE = 'var(--bobbee-on-tone, #fff)';

/** Translucent version of a color (hex or CSS variable), e.g. the background of a badge. */
export const tint = (color: string, percent: number): string =>
  `color-mix(in srgb, ${color} ${percent}%, transparent)`;

/** Statuses of pipelines and steps, with their tone and label. */
export const STATUSES = {
  running: { tone: 'info', label: 'running' },
  done: { tone: 'success', label: 'done' },
  failed: { tone: 'error', label: 'failed' },
  interrupted: { tone: 'purple', label: 'interrupted' },
  aborted: { tone: 'error', label: 'aborted' },
  pending: { tone: 'neutral', label: 'pending' },
  skipped: { tone: 'warning', label: 'skipped' },
} as const satisfies Record<string, { tone: Tone; label: string }>;

export type KnownStatus = keyof typeof STATUSES;

export const statusColor = (status: string): string =>
  TONE_COLORS[
    (STATUSES as Record<string, { tone: Tone }>)[status]?.tone ?? 'neutral'
  ];

export const statusLabel = (status: string): string =>
  (STATUSES as Record<string, { label: string }>)[status]?.label ?? status;

interface StatusChipProps {
  /** A known pipeline/step status, or use `tone` with a `label`. */
  status?: string;
  tone?: Tone;
  label?: string;
  variant?: 'badge' | 'chip' | 'dot';
  /** Makes the badge a toggle button (e.g. to filter by this value). Badge variant only. */
  onClick?: () => void;
  /** Pressed state of a clickable badge. */
  active?: boolean;
}

/** One status display for the whole portal: colored chip, badge or dot. */
export function StatusChip({
  status,
  tone,
  label,
  variant = 'badge',
  onClick,
  active,
}: StatusChipProps) {
  const color = tone ? TONE_COLORS[tone] : statusColor(status ?? '');
  const text = label ?? (status ? statusLabel(status) : '');

  if (variant === 'dot') {
    return (
      <span
        role="img"
        aria-label={text}
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: color,
        }}
      />
    );
  }
  if (variant === 'chip') {
    return (
      <Chip
        size="small"
        label={text}
        style={{
          backgroundColor: tint(color, 12),
          color,
          fontWeight: 500,
          borderRadius: 6,
        }}
      />
    );
  }
  // Bobbee app pill: tinted background, tone border, square-ish corners. Only running
  // statuses keep a dot, to show activity.
  const pillStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '1px 8px',
    borderRadius: 6,
    border: `1px solid ${tint(color, active ? 80 : 30)}`,
    backgroundColor: tint(color, active ? 22 : 8),
    whiteSpace: 'nowrap',
  };
  const content = (
    <>
      {status === 'running' && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: color,
            boxShadow: `0 0 0 2px ${tint(color, 25)}`,
          }}
        />
      )}
      <span
        style={{ fontSize: 12, fontWeight: 500, lineHeight: '18px', color }}
      >
        {text}
      </span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        aria-pressed={Boolean(active)}
        onClick={onClick}
        style={{ ...pillStyle, font: 'inherit', cursor: 'pointer' }}
      >
        {content}
      </button>
    );
  }
  return <span style={pillStyle}>{content}</span>;
}
